'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import { deliverForm, formEndpoints } from '@/lib/forms';
import { company } from '@/lib/company';

const supportSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  product: z.string().min(1, 'Please select a product'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

type SupportFormData = z.infer<typeof supportSchema>;

// Products this corporation actually supports, plus the shipped apps that
// still point users here. Historical studio-era names are kept where a live
// app or store listing still directs people to this page.
const products = [
  { value: '', label: 'Select a product or service' },
  { value: 'dilacrm', label: 'DilaCRM / Sales24' },
  { value: 'dilaerp', label: 'DilaERP' },
  { value: 'dilamenu', label: 'dila.menu / dila RMS' },
  { value: 'sooqsolar', label: 'SooqSolar' },
  { value: 'cr-medical', label: 'CR Medical' },
  { value: 'kurdish-calendar', label: 'Kurdish Calendar' },
  { value: 'taakline', label: 'TAAKLINE' },
  { value: 'zubene', label: 'Zubene' },
  { value: 'web-development', label: 'Web development' },
  { value: 'mobile-app', label: 'Mobile app development' },
  { value: 'ai-integration', label: 'AI integration' },
  { value: 'other', label: 'Other' },
];

export function SupportForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [channel, setChannel] = useState<'mailto' | 'endpoint'>('mailto');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SupportFormData>({
    resolver: zodResolver(supportSchema),
  });

  const onSubmit = async (data: SupportFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Delivers via the visitor's mail client unless this deployment has an
      // endpoint explicitly configured. It never posts to the DataCode
      // backend — see src/lib/forms.ts. App-specific account-deletion and
      // Zubene support forms keep their own integrations.
      const result = await deliverForm({
        endpoint: formEndpoints.support,
        subject: `Support request: ${data.product}`,
        fields: [
          ['Product', data.product],
          ['Name', data.name],
          ['Email', data.email],
          ['Message', data.message],
        ],
        payload: data,
      });

      setChannel(result.channel);

      if (result.ok) {
        setSubmitStatus('success');
        trackEvent('support_submit', { status: 'success', product: data.product, channel: result.channel });
        reset();
      } else {
        setSubmitStatus('error');
        trackEvent('support_submit', { status: 'error', product: data.product, channel: result.channel });
      }
    } catch {
      setSubmitStatus('error');
      trackEvent('support_submit', { status: 'error', product: data.product });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Two column layout for Name and Email */}
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-16 mb-16">
        {/* Name Field */}
        <div>
          <input
            {...register('name')}
            type="text"
            placeholder="What's Your Name"
            className={cn(
              'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors placeholder:text-white/80',
              errors.name ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
          />
          {errors.name && (
            <p className="mt-2 text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <input
            {...register('email')}
            type="email"
            placeholder="Your E-mail"
            className={cn(
              'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors placeholder:text-white/80',
              errors.email ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
          />
          {errors.email && (
            <p className="mt-2 text-sm text-red-500">{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* Product Selection - Full Width */}
      <div className="mb-16">
        <select
          {...register('product')}
          className={cn(
            'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors appearance-none cursor-pointer',
            errors.product ? 'border-red-500' : 'border-white/30 focus:border-white',
            'bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'white\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")] bg-no-repeat bg-[right_0_center] bg-[length:24px]'
          )}
          style={{ backgroundColor: 'transparent' }}
        >
          {products.map((product) => (
            <option
              key={product.value}
              value={product.value}
              style={{ backgroundColor: '#173835', color: 'white' }}
            >
              {product.label}
            </option>
          ))}
        </select>
        {errors.product && (
          <p className="mt-2 text-sm text-red-500">{errors.product.message}</p>
        )}
      </div>

      {/* Message Field - Full Width */}
      <div className="mb-16">
        <textarea
          {...register('message')}
          rows={3}
          placeholder="Describe your issue or question"
          className={cn(
            'w-full resize-none border-b bg-transparent py-4 text-lg text-white outline-none transition-colors placeholder:text-white/80',
            errors.message ? 'border-red-500' : 'border-white/30 focus:border-white'
          )}
        />
        {errors.message && (
          <p className="mt-2 text-sm text-red-500">{errors.message.message}</p>
        )}
      </div>

      {/* Submit Button - Centered */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '48px' }}>
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            border: '1px solid #fff',
            backgroundColor: 'transparent',
            paddingLeft: '60px',
            paddingRight: '60px',
            paddingTop: '20px',
            paddingBottom: '20px',
            fontSize: '16px',
            fontWeight: '500',
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            minWidth: '220px',
          }}
          className="hover:bg-white hover:text-[#173835] disabled:opacity-50"
        >
          {isSubmitting ? 'Sending...' : 'Submit Request'}
        </button>
      </div>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <p className="mt-8 text-center text-green-400">
          {channel === 'mailto'
            ? `Your email app should now be open, addressed to ${company.email}.`
            : 'Thank you — your support request has been submitted.'}
        </p>
      )}
      {submitStatus === 'error' && (
        <p className="mt-8 text-center text-red-500">
          {`Something went wrong. Please email us directly at ${company.email}.`}
        </p>
      )}
    </form>
  );
}
